/* eslint-disable max-lines-per-function */
import React from "react"
import { useDispatch, useSelector } from "react-redux"
import styled from "styled-components"
import { AnimatePresence, motion } from "framer-motion"

import { useInterval, usePubSubListener, useQueue, useSoundEffect } from "/src/hooks"
import { flexUsed } from "/src/redux/actions/flexUsed"
import { FLEX_DISPATCH_INTERVAL, MAX_ANIMATEABLE_FLEXES } from "/src/constants"
import { getRandomIntegerBelow } from "/src/helpers"
import Flex from "/src/components/flex/Flex"


import OOO from "/media/OOO.mp3"
import WOOOH from "/media/WOOOH.mp3"
import YOOOOO from "/media/YOOOOO.mp3"

import VERY_NICE from "/media/Borat_Very_Nice.mp3"

const Background = styled.div`
  margin-right: 27vw;
  height: 50vh;
  display: grid;
  grid-template-columns: 1fr 3fr 1fr;
  grid-template-rows: 1fr 3fr;
`
const CenterFlexArea = styled.div`
  position: relative;
  height: 100%;
  grid-row: 1 / span 1;
  grid-column: 2 / span 1;

  perspective: 50px;
  perspective-origin: center;
`
const LeftFlexArea = styled(CenterFlexArea)`
  grid-row: 1 / span 2;
  grid-column: 1 / span 1;
`
const RightFlexArea = styled(LeftFlexArea)`
  grid-column: 3 / span 1;
`
const animations = {
  initial: position => ({
    z: -50,
    opacity: 0,

    filter: "brightness(1)",

    position: "absolute",
    left: `${position.x}%`,
    top: `${position.y}%`
  }),
  enter: {
    z: -10,
    opacity: 1,
    filter: "brightness(0.66)",

    transition: {
      delay: 0.2,
      filter: { delay: 8 },
      z: {
        type: "spring",
        stiffness: 300
      }
    },
  },
  exit: {
    z: -500,
    opacity: 0
  }
}

const colors = ["blue", "orange", "pink"]
const channelId = window.location.hash.slice(1)

const FlexOverlay = () => {

  const flexes = useSelector(state => state.flexes.active)
  const dispatch = useDispatch()

  const sfxVariants = [
    useSoundEffect(OOO, 0.7),
    useSoundEffect(WOOOH, 0.5), // purposefully reduced to sound less harsh
    useSoundEffect(YOOOOO, 0.7)
  ]

  const activateFlex = flex => {
    if (!flex) return

    dispatch(flexUsed(flex))

    // If none of sfxVariants are already playing
    if (!sfxVariants.some(soundEffect => soundEffect.isPlaying)) {
      sfxVariants[getRandomIntegerBelow(3)].play()
    }
  }

  const flexQueue = useQueue([])
  const activateNextFlex = () => activateFlex(flexQueue.next())

  // When received from pubsub, add flex to queue
  usePubSubListener(channelId, data => {
    if (data.username) {
      flexQueue.add({
        ...data,
        assignedArea: getRandomIntegerBelow(3),
        color: colors[getRandomIntegerBelow(colors.length)],
        position: { x: getRandomIntegerBelow(100), y: getRandomIntegerBelow(100) },
        rank: data.rank ?? "-",
        score: data.score ?? 0
      })
    }
  })

  // Every FLEX_DISPATCH_INTERVAL ms, dispatch flex from queue
  useInterval(activateNextFlex, FLEX_DISPATCH_INTERVAL)

  return (
    <Background>
      {
        [LeftFlexArea, CenterFlexArea, RightFlexArea].map((Area, i) => (
          <Area key={i}>
            <AnimatePresence>
              {
                flexes
                  .map((flex, i) => ({
                    ...flex,
                    // Only the most recent flexes are animated
                    isAnimated: i >= (flexes.length - MAX_ANIMATEABLE_FLEXES)
                  }))
                  .filter(({ assignedArea }) => assignedArea === i)
                  .map(({ id, isAnimated, color, position, rank, score, username }) => (
                    <motion.div
                      animate="enter"
                      custom={position}
                      exit="exit"
                      initial="initial"
                      key={id}
                      variants={animations}
                    >
                      <Flex
                        color={color}
                        disableAnimation={!isAnimated}
                        rank={rank}
                        score={score}
                        username={username}
                      />
                    </motion.div>
                  ))
              }
            </AnimatePresence>
          </Area>
        ))
      }
    </Background>
  )
}

export default FlexOverlay